import { useState, useMemo, useEffect } from "react";
import {
  TrendingUp, ChevronRight, LogOut, X, Search as SearchIcon,
  Lock, Gift, Sparkles, Star, StarOff,
} from "lucide-react";

/**
 * DashboardSidebar (V164.3 redesign)
 * --------------------------------------------------------------------------
 * Compact, searchable sidebar with:
 * - Live search across all nav items
 * - Pinnable favourites (persisted in localStorage)
 * - Left-border active indicator (modern look)
 * - Smooth section collapse
 * - Glass backdrop on mobile drawer
 *
 * Pure presentation — receives data + callbacks via props so the parent
 * Dashboard page keeps owning state (activeTab, sidebarOpen, user, etc.).
 *
 * IMPORTANT: All existing `data-testid` attributes are preserved so the
 * frontend test suite remains green.
 */
const PINNED_STORAGE_KEY = "trp_sidebar_pinned_v1";

const planLabels = {
  free: "Free", starter: "Pro", pro: "Pro",
  business: "Business", enterprise: "Enterprise",
};
const planColors = {
  free: "bg-gray-100 text-gray-700",
  starter: "bg-blue-100 text-blue-700",
  pro: "bg-blue-100 text-blue-700",
  business: "bg-purple-100 text-purple-700",
  enterprise: "bg-amber-100 text-amber-700",
};

function readPinned() {
  try { return JSON.parse(localStorage.getItem(PINNED_STORAGE_KEY) || "[]"); }
  catch { return []; }
}

function writePinned(ids) {
  try { localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(ids)); } catch {}
}

export default function DashboardSidebar({
  user, t, activeTab, setActiveTab,
  sidebarOpen, setSidebarOpen,
  mainItems, collapsibleSections,
  expandedSections, toggleSection,
  logout,
}) {
  const [query, setQuery] = useState("");
  const [pinned, setPinned] = useState(readPinned);

  // Persist pinned items
  useEffect(() => { writePinned(pinned); }, [pinned]);

  const togglePin = (id) => {
    setPinned((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Flatten ALL items so search can match across sections
  const allItems = useMemo(() => {
    const flat = mainItems.map(it => ({ ...it, _section: "main" }));
    collapsibleSections.forEach(sec => {
      sec.items.forEach(it => flat.push({ ...it, _section: sec.key, _sectionLabel: sec.label }));
    });
    return flat;
  }, [mainItems, collapsibleSections]);

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    return allItems.filter(it => (it.label || "").toLowerCase().includes(q)).slice(0, 12);
  }, [q, allItems]);

  // Pinned items (resolved from full item list)
  const pinnedItems = useMemo(() => {
    if (!pinned.length) return [];
    const map = new Map(allItems.map(it => [it.id, it]));
    return pinned.map(id => map.get(id)).filter(Boolean);
  }, [pinned, allItems]);

  const isActive = (id) => activeTab === id;

  const renderItem = (item, opts = {}) => {
    const { compact = false, allowPin = true } = opts;
    const Icon = item.icon;
    const active = !item.href && isActive(item.id);
    const pinnedNow = pinned.includes(item.id);

    const content = (
      <>
        <span
          className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r transition-all ${
            active ? "bg-blue-600" : "bg-transparent"
          }`}
          aria-hidden
        />
        <Icon className={`w-4 h-4 shrink-0 transition-colors ${
          active ? "text-blue-600" : item.highlight ? "text-emerald-600" : "text-gray-500 group-hover:text-gray-700"
        }`} />
        <span className="flex-1 text-left truncate">{item.label}</span>
        {item.badge && (
          <span className={`text-[10px] px-1.5 py-0.5 font-bold shrink-0 rounded ${item.badgeColor || "bg-gray-100 text-gray-600"}`}>
            {item.badge}
          </span>
        )}
        {item.highlight && !active && !item.badge && <Gift className="w-3.5 h-3.5 text-emerald-500" />}
        {allowPin && !item.href && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); togglePin(item.id); }}
            className={`shrink-0 -mr-1 p-0.5 rounded transition-opacity ${
              pinnedNow ? "opacity-100 text-amber-500" : "opacity-0 group-hover:opacity-100 text-gray-400 hover:text-amber-500"
            }`}
            data-testid={`sidebar-pin-${item.id}`}
            aria-label={pinnedNow ? "Unpin" : "Pin"}
          >
            {pinnedNow ? <Star className="w-3.5 h-3.5 fill-current" /> : <StarOff className="w-3.5 h-3.5" />}
          </button>
        )}
      </>
    );

    const baseCls = `group relative w-full flex items-center gap-3 ${compact ? "px-3 py-1.5" : "px-3 py-2"} text-sm rounded-md transition-colors ${
      active
        ? "bg-blue-50 text-blue-700 font-medium"
        : item.highlight && !active
        ? "text-emerald-700 bg-emerald-50/60 hover:bg-emerald-50 font-medium"
        : "text-gray-600 hover:bg-gray-100"
    }`;

    if (item.href) {
      return (
        <a key={item.id} href={item.href} className={baseCls} data-testid={`sidebar-${item.id}`}>
          {content}
        </a>
      );
    }
    return (
      <button
        key={item.id}
        onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
        className={baseCls}
        data-testid={`sidebar-${item.id}`}
      >
        {content}
      </button>
    );
  };

  return (
    <>
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-white border-r border-gray-200 z-40 flex flex-col transition-transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="dashboard-sidebar"
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 flex items-center justify-center rounded">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-black font-['Chivo']">TopRankPulse</span>
          </a>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400" aria-label="Close sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User card */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
          ) : (
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm rounded-full">
              {(user.name || user.email)[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 truncate">{user.name || user.email.split("@")[0]}</p>
            <span className={`inline-block text-[10px] px-1.5 py-0.5 font-bold rounded ${planColors[user.plan] || planColors.free}`}>
              {planLabels[user.plan] || planLabels.free}
            </span>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pt-3 pb-2 shrink-0">
          <div className="relative">
            <SearchIcon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.sb_search || "Search…"}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-md placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white"
              data-testid="sidebar-search"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label="Clear">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        <nav className="px-3 pb-3 space-y-0.5 flex-1 overflow-y-auto scrollbar-thin">
          {/* Search results override everything */}
          {searchResults ? (
            <>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-3 pt-1 pb-1">
                {(t.sb_results || "Results")} ({searchResults.length})
              </p>
              {searchResults.length === 0 ? (
                <p className="text-xs text-gray-400 px-3 py-3">{t.sb_no_results || "No matching items"}</p>
              ) : (
                searchResults.map(it => renderItem(it, { compact: true }))
              )}
            </>
          ) : (
            <>
              {/* Pinned section */}
              {pinnedItems.length > 0 && (
                <div className="mb-1">
                  <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider px-3 pt-1 pb-1 flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    {t.sb_pinned || "Pinned"}
                  </p>
                  {pinnedItems.map(it => renderItem(it, { compact: true }))}
                  <div className="mx-3 my-2 border-t border-gray-100" />
                </div>
              )}

              {/* Main items */}
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-3 pt-1 pb-1">
                {t.sb_main || "Main"}
              </p>
              {mainItems.map(it => renderItem(it))}

              {/* Collapsible sections */}
              {collapsibleSections.map((section) => {
                const isOpen = !!expandedSections[section.key];
                const hasActive = section.items.some(it => !it.href && activeTab === it.id);
                return (
                  <div key={section.key}>
                    <button
                      type="button"
                      onClick={() => toggleSection(section.key)}
                      className="w-full flex items-center justify-between px-3 pt-4 pb-1 group"
                      data-testid={`section-toggle-${section.key}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${
                        hasActive ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"
                      }`}>
                        {section.label}
                      </span>
                      <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                    </button>
                    <div className={`grid transition-all duration-200 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden space-y-0.5">
                        {section.items.map(it => renderItem(it, { compact: true }))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Admin */}
              {user?.role === "admin" && (
                <>
                  <p className="text-[10px] font-bold text-purple-500 uppercase tracking-wider px-3 pt-4 pb-1">Admin</p>
                  <button
                    onClick={() => { setActiveTab("admin"); setSidebarOpen(false); }}
                    className={`group relative w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                      activeTab === "admin" ? "bg-purple-50 text-purple-700 font-medium" : "text-gray-600 hover:bg-gray-100"
                    }`}
                    data-testid="sidebar-admin"
                  >
                    <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r ${
                      activeTab === "admin" ? "bg-purple-600" : "bg-transparent"
                    }`} aria-hidden />
                    <Lock className="w-4 h-4 shrink-0" />
                    <span className="flex-1 text-left">Admin Panel</span>
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                  </button>
                </>
              )}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 shrink-0">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-md transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>{t.sb_sign_out || "Sign Out"}</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-30 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
}
